import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { LogTemplateType } from '@/__generated__/graphql';
import { useAtom } from 'jotai';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { SharedLogsTemplateForm } from './SharedLogsTemplateForm';
import { QaqcTemplateForm } from './QaqcTemplateForm';
import { IndirectPobTemplateForm } from './IndirectPobTemplateForm';
import { workpackTabStyles, FloatingLine } from './styles';
import { selectedLogsTemplateType } from '../state';

const LOG_TEMPLATE_TAB_TYPES: LogTemplateType[] = [
  LogTemplateType.WorklogTemplate,
  LogTemplateType.CostLogTemplate,
  LogTemplateType.IndirectPobTemplate,
  LogTemplateType.QaqcTemplate,
];

type LogsTemplateFormProps = {
  structureId: string;
  currentStructureName: string;
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
};

export const LogsTemplateForm = ({
  structureId,
  currentStructureName,
  emptyForm,
  handleEmptyForm,
}: LogsTemplateFormProps) => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedTemplateType, setSelectedTemplateType] = useAtom(selectedLogsTemplateType);

  const handleTabChange = (selectedTabIndex: number) => {
    setSelectedTab(selectedTabIndex);
    const templateType =
      LOG_TEMPLATE_TAB_TYPES[selectedTabIndex] ?? LogTemplateType.WorklogTemplate;
    setSelectedTemplateType(templateType);
  };

  useEffect(() => {
    const tabIndex = LOG_TEMPLATE_TAB_TYPES.indexOf(selectedTemplateType);
    if (tabIndex !== -1 && tabIndex !== selectedTab) {
      setSelectedTab(tabIndex);
    }
  }, [selectedTemplateType, selectedTab]);

  const tabs = useMemo(() => {
    return [
      {
        index: 0,
        label: 'Worklogs',
        component: (
          <SharedLogsTemplateForm
            emptyForm={emptyForm}
            handleEmptyForm={handleEmptyForm}
            structureId={structureId}
            currentStructureName={currentStructureName}
            hostLogTemplateType={LogTemplateType.WorklogTemplate}
          />
        ),
        disabled: false,
        testid: 'worklogs-template-tab',
      },
      {
        index: 1,
        label: 'Cost Logging',
        component: (
          <SharedLogsTemplateForm
            emptyForm={emptyForm}
            handleEmptyForm={handleEmptyForm}
            structureId={structureId}
            currentStructureName={currentStructureName}
            hostLogTemplateType={LogTemplateType.CostLogTemplate}
          />
        ),
        disabled: false,
        testid: 'cost-logging-template-tab',
      },
      {
        index: 2,
        label: 'Indirect POB',
        component: (
          <IndirectPobTemplateForm
            emptyForm={emptyForm}
            handleEmptyForm={handleEmptyForm}
            structureId={structureId}
            currentStructureName={currentStructureName}
          />
        ),
        disabled: false,
        testid: 'indirect-pob-template-tab',
      },
      {
        index: 3,
        label: 'QA/QC',
        component: (
          <QaqcTemplateForm
            emptyForm={emptyForm}
            handleEmptyForm={handleEmptyForm}
            structureId={structureId}
            currentStructureName={currentStructureName}
          />
        ),
        disabled: false,
        testid: 'qaqc-template-tab',
      },
    ];
  }, [currentStructureName, structureId, emptyForm, handleEmptyForm]);

  return (
    <Box>
      <BasicTabs
        tabs={tabs}
        selectedTab={selectedTab}
        changeTab={handleTabChange}
        tabsStyles={workpackTabStyles}
        customSeperator={<FloatingLine />}
        renderingStrategy="eager"
      />
    </Box>
  );
};
